import React, { useState, useEffect } from 'react';
import { X, Plus, Save, Loader, Filter } from 'lucide-react';
import { doc, setDoc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';

interface ClassifierRulesModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
}

interface ClassifierConfig {
  minTitleLength: number;
  hideLlmUnplaceable: boolean;
  compatibilityTerms: string[];
  outOfTreeTerms: string[];
  firmwareTerms: string[];
  accessoryStartTerms: string[];
  treeSubcategories: string[];
}

const EMPTY_CONFIG: ClassifierConfig = {
  minTitleLength: 15,
  hideLlmUnplaceable: true,
  compatibilityTerms: [],
  outOfTreeTerms: [],
  firmwareTerms: [],
  accessoryStartTerms: [],
  treeSubcategories: [],
};

// Editor de una lista de términos (pills acumulables removibles + input).
const TermListEditor: React.FC<{
  label: string;
  hint: string;
  terms: string[];
  onChange: (terms: string[]) => void;
  pillClass?: string;
}> = ({ label, hint, terms, onChange, pillClass = 'bg-blue-100 text-blue-700' }) => {
  const [input, setInput] = useState('');

  const add = () => {
    const val = input.trim().toLowerCase();
    if (!val) return;
    if (terms.some(t => t.toLowerCase() === val)) { setInput(''); return; }
    onChange([...terms, input.trim()]);
    setInput('');
  };

  const remove = (idx: number) => {
    onChange(terms.filter((_, i) => i !== idx));
  };

  return (
    <div className="border border-gray-200 rounded-lg p-3">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-sm font-medium text-gray-800">{label}</span>
        <span className="text-xs text-gray-400 tabular-nums">{terms.length}</span>
      </div>
      <p className="text-xs text-gray-500 mb-2">{hint}</p>
      <div className="flex flex-wrap gap-1.5 mb-2 max-h-32 overflow-y-auto">
        {terms.length === 0 && (
          <span className="text-xs text-gray-400 italic">Sin términos</span>
        )}
        {terms.map((t, idx) => (
          <span
            key={`${t}-${idx}`}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${pillClass}`}
          >
            {t}
            <button
              onClick={() => remove(idx)}
              className="hover:text-red-600"
              title="Quitar"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder="Agregar término y Enter..."
          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <button
          onClick={add}
          disabled={!input.trim()}
          className="px-2 py-1 text-gray-600 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40"
          title="Agregar"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const ClassifierRulesModal: React.FC<ClassifierRulesModalProps> = ({
  isOpen,
  onClose,
  clientId,
}) => {
  const [config, setConfig] = useState<ClassifierConfig>(EMPTY_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const load = async () => {
      setLoading(true);
      try {
        const ref = doc(db, 'clients', clientId, 'catalogRules', 'classifier');
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const d = snap.data() as Partial<ClassifierConfig>;
          setConfig({
            minTitleLength: d.minTitleLength ?? EMPTY_CONFIG.minTitleLength,
            hideLlmUnplaceable: d.hideLlmUnplaceable ?? EMPTY_CONFIG.hideLlmUnplaceable,
            compatibilityTerms: d.compatibilityTerms ?? [],
            outOfTreeTerms: d.outOfTreeTerms ?? [],
            firmwareTerms: d.firmwareTerms ?? [],
            accessoryStartTerms: d.accessoryStartTerms ?? [],
            treeSubcategories: d.treeSubcategories ?? [],
          });
        } else {
          setConfig(EMPTY_CONFIG);
        }
      } catch (err) {
        console.error('Error loading classifier config:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isOpen, clientId]);

  const save = async () => {
    setSaving(true);
    try {
      const ref = doc(db, 'clients', clientId, 'catalogRules', 'classifier');
      await setDoc(ref, {
        ...config,
        minTitleLength: Number(config.minTitleLength) || 0,
        updatedAt: Timestamp.now(),
      }, { merge: true });
      onClose();
    } catch (err) {
      console.error('Error saving classifier config:', err);
      alert('Error al guardar las reglas de clasificación');
    } finally {
      setSaving(false);
    }
  };

  const set = <K extends keyof ClassifierConfig>(key: K, val: ClassifierConfig[K]) =>
    setConfig(prev => ({ ...prev, [key]: val }));

  if (!isOpen) return null;

  const totalTerms =
    config.compatibilityTerms.length + config.outOfTreeTerms.length +
    config.firmwareTerms.length + config.accessoryStartTerms.length;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        <div className="inline-block w-full max-w-4xl px-4 pt-5 pb-4 overflow-visible text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                Reglas de Clasificación
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Términos para detectar productos fuera del catálogo (out-of-scope). Se ocultan automáticamente al cerrar cada toma.
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <X className="w-6 h-6" />
            </button>
          </div>

          {loading ? (
            <div className="py-12 text-center">
              <Loader className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">Cargando reglas...</p>
            </div>
          ) : (
            <>
              <div className="max-h-[60vh] overflow-y-auto pr-1 space-y-3">
                {/* Parámetros estructurales */}
                <div className="flex flex-wrap gap-4 items-center border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <label className="flex items-center gap-2 text-sm text-gray-800">
                    Largo mínimo de título
                    <input
                      type="number"
                      min={0}
                      value={config.minTitleLength}
                      onChange={(e) => set('minTitleLength', Number(e.target.value))}
                      className="w-20 px-2 py-1 text-sm border border-gray-300 rounded tabular-nums"
                    />
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-800">
                    <input
                      type="checkbox"
                      checked={config.hideLlmUnplaceable}
                      onChange={(e) => set('hideLlmUnplaceable', e.target.checked)}
                      className="rounded"
                    />
                    Ocultar items que el LLM no pudo ubicar (subcategoría vacía)
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <TermListEditor
                    label="Subcategorías válidas del árbol"
                    hint="Items con estas subcategorías están EN SCOPE: nunca se ocultan por heurística de título."
                    terms={config.treeSubcategories}
                    onChange={(t) => set('treeSubcategories', t)}
                    pillClass="bg-green-100 text-green-700"
                  />
                  <TermListEditor
                    label="Fuera del árbol"
                    hint="Productos de otra categoría (ej. electrodomésticos ajenos al catálogo)."
                    terms={config.outOfTreeTerms}
                    onChange={(t) => set('outOfTreeTerms', t)}
                  />
                  <TermListEditor
                    label="Accesorios / compatibilidad"
                    hint='Ej. "compatible con", "funda para", "vidrio templado". (No se auto-ocultan por default.)'
                    terms={config.compatibilityTerms}
                    onChange={(t) => set('compatibilityTerms', t)}
                  />
                  <TermListEditor
                    label="Firmware / software / seguros"
                    hint='Ej. "firmware", "garantía extendida", "seguro de".'
                    terms={config.firmwareTerms}
                    onChange={(t) => set('firmwareTerms', t)}
                  />
                  <TermListEditor
                    label="Accesorio al inicio del título"
                    hint='Términos que indican accesorio si arrancan el título (ej. "Soporte para TV").'
                    terms={config.accessoryStartTerms}
                    onChange={(t) => set('accessoryStartTerms', t)}
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="mt-6 flex justify-between items-center">
                <div className="text-sm text-gray-500 flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  <span className="tabular-nums">{totalTerms}</span> términos de filtro ·
                  <span className="tabular-nums">{config.treeSubcategories.length}</span> subcats en árbol
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                    disabled={saving}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={save}
                    className="flex items-center gap-2 px-4 py-2 text-white bg-blue-500 rounded-lg hover:bg-blue-600"
                    disabled={saving}
                  >
                    {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Guardar Reglas
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClassifierRulesModal;
